import React from "react";
import {
  useController,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";
import {
  Checkbox,
  FormLabel,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import { SelectGroupedOptions } from "./type";

const Error = styled.div`
  color: ${({ theme }) => theme.palette.error.main};
  padding-top: ${({ theme }) => theme.spacing(1)};
`;

const StyledSelect = styled(Select)`
  height: 36px;
`;

const StyledListSubheader = styled(ListSubheader, {
  shouldForwardProp: (prop) => prop !== "isFirst",
})<{ isFirst: boolean }>`
  font-size: ${({ theme }) => theme.typography.overline.fontSize};
  line-height: ${({ theme }) => theme.typography.overline.lineHeight};
  font-weight: bold;
  color: ${({ theme }) => theme.palette.text.secondary};
  display: flex;
  align-items: center;
  text-transform: uppercase;
  height: 28px;
  ${({ isFirst, theme }) =>
    !isFirst && `border-top: 1px solid ${theme.palette.border.secondary};`}
`;

const StyledMenuItem = styled(MenuItem)`
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  line-height: ${({ theme }) => theme.typography.caption.lineHeight};
`;

const renderGroupOptions = (options: SelectGroupedOptions[]) => {
  return options.flatMap((group, index) => [
    <StyledListSubheader key={group.id} isFirst={index === 0}>
      {group.name}
    </StyledListSubheader>,
    ...group.options.map((option) => (
      <StyledMenuItem key={option.id} value={option.id}>
        {option.name}
      </StyledMenuItem>
    )),
  ]);
};

const renderOptions = (
  options: Record<string, string> | string[],
  currentValue: string[] | string,
  isMultiple?: boolean
) => {
  const optionEntries = Array.isArray(options)
    ? options.map((o) => [o, o])
    : Object.entries(options);

  return optionEntries.map(([key, value]) => {
    const id = Array.isArray(options) ? value : key;

    return (
      <MenuItem key={id} value={id} data-testid={id}>
        {isMultiple && (
          <Checkbox
            checked={(currentValue as string[])?.includes(id)}
            sx={{ marginRight: (theme) => theme.spacing(3) }}
          />
        )}
        {value}
      </MenuItem>
    );
  });
};

type SelectLocalProps = React.DetailedHTMLProps<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  HTMLSelectElement
>;

type FormSelectProps<T extends FieldValues> = SelectLocalProps &
  SelectProps &
  UseControllerProps<T> & {
    options?: Record<string, string> | string[] | SelectGroupedOptions[];
    optional?: boolean;
  };

export const RHFSelect = <T extends FieldValues>(
  props: FormSelectProps<T>
): React.ReactNode => {
  const {
    control,
    name,
    defaultValue,
    label = "",
    type = "text",
    options,
    children,
    optional = false,
    ...rest
  } = props;

  const {
    field,
    fieldState: { error, invalid },
  } = useController({ control, name, defaultValue });

  const isGrouped =
    Array.isArray(options) &&
    options.length > 0 &&
    Object.prototype.hasOwnProperty.call(options[0], "options");

  return (
    <div>
      {label && (
        <FormLabel htmlFor={name}>
          {typeof label === "string" ? (
            <Typography variant="body3">
              {label}
              {optional ? (
                <Typography
                  color="textSecondary"
                  component="span"
                  variant="body3"
                >
                  {" "}
                  (optional)
                </Typography>
              ) : (
                ""
              )}
            </Typography>
          ) : (
            label
          )}
        </FormLabel>
      )}
      <StyledSelect
        variant="outlined"
        fullWidth
        {...field}
        value={field.value || ""}
        error={invalid}
        type={type}
        onChange={(e: SelectChangeEvent<unknown>) =>
          field.onChange(e.target.value)
        }
        MenuProps={{
          disablePortal: false,
          sx: {
            "& .MuiBackdrop-root": {
              opacity: `0 !important`,
              cursor: "initial",
            },
          },
        }}
        sx={{
          "& .MuiSelect-select": {
            padding: "8px 16px",
          },
        }}
        {...rest}
      >
        {options
          ? isGrouped
            ? renderGroupOptions(options as SelectGroupedOptions[])
            : renderOptions(
                options as Record<string, string> | string[],
                field.value as string | string[],
                rest.multiple
              )
          : children}
      </StyledSelect>
      {error?.message && (
        <Error>
          <Typography
            variant="caption"
            color="inherit"
            data-testid="rhf-select-error"
          >
            {error.message}
          </Typography>
        </Error>
      )}
    </div>
  );
};
